/**
 * Thin monoline glyph icons. No emoji anywhere in the project — a glyph is
 * always an Icon here. Stroke-based (currentColor), 24x24 viewBox, sized via
 * the `size` prop. Add new glyphs to the map below.
 *
 * A few glyphs are copied verbatim from the reference (ats.recruto.ru), where
 * they are drawn on a 20x20 grid; they carry their own box in VIEWBOX instead
 * of being redrawn, so the strokes stay exactly as the reference has them.
 */

export type IconName =
  | "search"
  | "sidebar"
  | "panel-right"
  | "briefcase"
  | "users"
  | "message"
  | "video"
  | "user"
  | "building"
  | "file-text"
  | "history"
  | "wallet"
  | "template"
  | "automation"
  | "settings"
  | "logout"
  | "plus"
  | "calendar"
  | "headcount"
  | "chevron-down"
  | "trash"
  | "grip"
  | "arrow-up"
  | "arrow-down"
  | "upload"
  | "close"
  | "alert"
  | "image"
  | "music"
  | "archive"
  | "chevron-up"
  | "minimize"
  | "expand"
  | "retry"
  | "eye"
  | "eye-off"
  | "chevron-left"
  | "chevron-right"
  | "download"
  | "external-link"
  | "check"
  | "play"
  | "pause"
  | "mic"
  | "phone"
  | "headphones"
  | "keyhole"
  | "send"
  | "share"
  | "link"
  | "bell"
  | "pulse"
  | "users-all"
  | "user-x"
  | "sliders"
  | "search-sm"
  | "stage"
  | "flag"
  | "grid"
  | "list"
  | "traffic"
  | "puzzle";

const PATHS: Record<IconName, JSX.Element> = {
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </>
  ),
  sidebar: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="1.5" />
      <path d="M9 4v16" />
    </>
  ),
  "panel-right": (
    <>
      <rect x="3" y="4" width="18" height="16" rx="1.5" />
      <path d="M15 4v16" />
    </>
  ),
  briefcase: (
    <>
      <rect x="3" y="7" width="18" height="13" rx="1.5" />
      <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="8" r="3" />
      <path d="M3.5 20a5.5 5.5 0 0 1 11 0" />
      <path d="M16 5.5a3 3 0 0 1 0 5.5" />
      <path d="M17 15.5a5.5 5.5 0 0 1 3.5 4.5" />
    </>
  ),
  message: (
    <path d="M4 5h16a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H9l-4 3v-3H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z" />
  ),
  video: (
    <>
      <rect x="3" y="6" width="12" height="12" rx="1.5" />
      <path d="m15 10 6-3v10l-6-3" />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5 20a7 7 0 0 1 14 0" />
    </>
  ),
  building: (
    <>
      <rect x="5" y="3" width="14" height="18" rx="1" />
      <path d="M9 7h2M13 7h2M9 11h2M13 11h2M9 15h2M13 15h2" />
    </>
  ),
  "file-text": (
    <>
      <path d="M7 3h7l4 4v14H7z" />
      <path d="M14 3v4h4" />
      <path d="M10 13h5M10 16h5" />
    </>
  ),
  wallet: (
    <>
      <rect x="3" y="6" width="18" height="13" rx="1.5" />
      <path d="M3 10h18" />
      <circle cx="16.5" cy="14" r="1" fill="currentColor" stroke="none" />
    </>
  ),
  template: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="1.5" />
      <path d="M3 9h18M9 9v11" />
    </>
  ),
  automation: (
    <>
      <path d="M13 3 4 14h6l-1 7 9-11h-6z" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v3M12 19v3M22 12h-3M5 12H2M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1M18.4 18.4l-2.1-2.1M7.7 7.7 5.6 5.6" />
    </>
  ),
  logout: (
    <>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="M16 17l5-5-5-5M21 12H9" />
    </>
  ),
  plus: <path d="M12 5v14M5 12h14" />,
  calendar: (
    <>
      <rect x="3.5" y="5" width="17" height="16" rx="1.5" />
      <path d="M3.5 9h17M8 3v4M16 3v4" />
    </>
  ),
  headcount: (
    <>
      <circle cx="12" cy="8" r="3.2" />
      <path d="M6 20a6 6 0 0 1 12 0" />
    </>
  ),
  "chevron-down": <path d="m6 9 6 6 6-6" />,
  trash: (
    <>
      <path d="M4 7h16M10 4h4M9 7l1 13h4l1-13" />
    </>
  ),
  grip: (
    <path d="M9 6h.01M9 12h.01M9 18h.01M15 6h.01M15 12h.01M15 18h.01" strokeWidth={2.4} />
  ),
  "arrow-up": <path d="M12 19V5M6 11l6-6 6 6" />,
  history: (
    <>
      <path d="M3 8a9 9 0 1 1-2 5.5" />
      <path d="M3 3v5h5" />
      <path d="M12 8v4l3 2" />
    </>
  ),
  "arrow-down": <path d="M12 5v14M6 13l6 6 6-6" />,
  upload: <path d="M12 16V4M8 8l4-4 4 4M4 20h16" />,
  close: <path d="M6 6l12 12M6 18 18 6" />,
  alert: (
    <>
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
      <path d="M12 9v4M12 17h.01" />
    </>
  ),
  image: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="1.5" />
      <circle cx="8.5" cy="9" r="1.5" />
      <path d="m3 17 5-5 4 4 3-3 6 6" />
    </>
  ),
  music: (
    <>
      <path d="M9 18V6l11-2v12" />
      <circle cx="6.5" cy="18" r="2.5" />
      <circle cx="17.5" cy="16" r="2.5" />
    </>
  ),
  archive: (
    <>
      <rect x="3" y="4" width="18" height="4" rx="1" />
      <path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8" />
      <path d="M10 12h4" />
    </>
  ),
  "chevron-up": <path d="m6 15 6-6 6 6" />,
  minimize: <path d="M5 12h14" />,
  expand: (
    <>
      <path d="M8 3H3v5M3 3l6 6" />
      <path d="M16 21h5v-5M21 21l-6-6" />
    </>
  ),
  retry: (
    <>
      <path d="M21 12a9 9 0 1 1-3-6.7" />
      <path d="M21 3v6h-6" />
    </>
  ),
  eye: (
    <>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  // Тот же глаз, перечёркнутый: «не видно клиенту».
  "eye-off": (
    <>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
      <circle cx="12" cy="12" r="3" />
      <path d="M4 4l16 16" />
    </>
  ),
  "chevron-left": <path d="m15 6-6 6 6 6" />,
  "chevron-right": <path d="m9 6 6 6-6 6" />,
  download: (
    <>
      <path d="M12 4v12M8 12l4 4 4-4" />
      <path d="M4 20h16" />
    </>
  ),
  "external-link": (
    <>
      <path d="M14 4h6v6" />
      <path d="M20 4 10 14" />
      <path d="M18 13v6a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h6" />
    </>
  ),
  check: <path d="M5 12l4 4L19 6" />,
  link: (
    <>
      <path d="M10 13.5a4.5 4.5 0 0 0 6.4.4l3-3a4.5 4.5 0 0 0-6.4-6.4l-1.4 1.4" />
      <path d="M14 10.5a4.5 4.5 0 0 0-6.4-.4l-3 3a4.5 4.5 0 0 0 6.4 6.4l1.4-1.4" />
    </>
  ),
  play: <path d="M8 5v14l11-7z" fill="currentColor" stroke="none" />,
  pause: (
    <>
      <rect x="7" y="5" width="3.5" height="14" rx="1" fill="currentColor" stroke="none" />
      <rect x="13.5" y="5" width="3.5" height="14" rx="1" fill="currentColor" stroke="none" />
    </>
  ),
  mic: (
    <>
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0" />
      <path d="M12 18v3" />
    </>
  ),
  phone: (
    <path d="M8 3.9l2.1 3.9-2 1.8c.9 2 2.4 3.5 4.4 4.4l1.8-2 3.9 2.1-.7 3c-.2.8-.9 1.3-1.7 1.2-6-.7-10.5-5.2-11.4-11.2-.1-.8.4-1.5 1.2-1.7l2.4-.5z" />
  ),
  // Замочная скважина — «Подглядеть» во встречу (сисадмин, MeetingDrawer).
  keyhole: <path d="M12 5a3.6 3.6 0 0 0-1.95 6.63L9 19h6l-1.05-7.37A3.6 3.6 0 0 0 12 5z" />,
  headphones: (
    <>
      <path d="M4 16v-4a8 8 0 0 1 16 0v4" />
      <rect x="3" y="14" width="4.5" height="6.5" rx="1.8" />
      <rect x="16.5" y="14" width="4.5" height="6.5" rx="1.8" />
    </>
  ),
  share: (
    <>
      <path d="M9.6 14.4l4.8-4.8" />
      <path d="M10.5 6.9l1.8-1.8a3.7 3.7 0 015.3 5.3l-1.8 1.8" />
      <path d="M13.5 17.1l-1.8 1.8a3.7 3.7 0 01-5.3-5.3l1.8-1.8" />
    </>
  ),
  send: (
    <>
      <path d="M21 3 3 10.5l7 2.5 2.5 7z" />
      <path d="M21 3 10 14" />
    </>
  ),
  bell: (
    <>
      <path d="M6 16V11a6 6 0 0 1 12 0v5l1.5 2h-15z" />
      <path d="M10 20.5a2.2 2.2 0 0 0 4 0" />
    </>
  ),
  // 20x20 (see VIEWBOX): the inbox bar's «Активные / Все / Неактивные» and
  // «Расширенные фильтры».
  pulse: <path d="M2.5 10h3.2l1.8-4.2 2.6 8.4 1.9-4.2h5.5" />,
  // 20x20: пункт меню «Расширение», как в эталоне.
  puzzle: <path d="M8 3h4v2.2a1.8 1.8 0 1 0 3.6 0V3h1.4v14H3V3h1.4v2.2A1.8 1.8 0 1 0 8 5.2z" />,
  "users-all": (
    <>
      <circle cx="7.6" cy="7.4" r="2.6" />
      <path d="M3 15.4c0-2.2 2-3.6 4.6-3.6s4.6 1.4 4.6 3.6" />
      <path d="M13.4 5.4a2.4 2.4 0 010 4.4M15 15.4c0-1.6-.7-2.7-2-3.3" />
    </>
  ),
  "user-x": (
    <>
      <circle cx="9" cy="7.2" r="2.7" />
      <path d="M4.2 15.6c0-2.3 2.1-3.8 4.8-3.8 1 0 1.9.2 2.7.6" />
      <path d="M13.2 12.6l3.6 3.6M16.8 12.6l-3.6 3.6" />
    </>
  ),
  sliders: (
    <>
      <path d="M3 7h6.6M14.4 7H17M3 13h2.6M10.4 13H17" />
      <circle cx="12" cy="7" r="2.4" />
      <circle cx="8" cy="13" r="2.4" />
    </>
  ),
  // The reference's magnifier in the inbox search pill (drawn at 15px, stroke
  // 1.5): a smaller lens and a shorter handle than the 24-grid «search».
  "search-sm": (
    <>
      <circle cx="9" cy="9" r="5.5" />
      <path d="M13.5 13.5l4 4" />
    </>
  ),
  // Смена этапа воронки — ступени вверх (строка ленты, всплывашка владельцу).
  stage: (
    <>
      <path d="M3 19h5v-5h5V9h5" />
      <path d="M15 5.5 18 3l3 2.5M18 3v6" />
    </>
  ),
  // Кандидатура закрыта / снова в работе — флажок итога.
  flag: (
    <>
      <path d="M5 21V4" />
      <path d="M5 4h12l-2.5 4.5L17 13H5" />
    </>
  ),
  // Раскладка выдачи поиска: галерея карточек и таблица.
  grid: (
    <>
      <rect x="4" y="4" width="7" height="7" />
      <rect x="13" y="4" width="7" height="7" />
      <rect x="4" y="13" width="7" height="7" />
      <rect x="13" y="13" width="7" height="7" />
    </>
  ),
  list: <path d="M4 6h16M4 12h16M4 18h16" />,
  // Раздел «Трафик» — воронка публикаций hh.ru.
  traffic: <path d="M4 4h16l-6 8v6l-4 2v-8z" />,
};

const VIEWBOX: Partial<Record<IconName, string>> = {
  pulse: "0 0 20 20",
  "users-all": "0 0 20 20",
  "user-x": "0 0 20 20",
  sliders: "0 0 20 20",
  "search-sm": "0 0 20 20",
  puzzle: "0 0 20 20",
};

export function Icon({
  name,
  size = 18,
  className,
  strokeWidth = 1.6,
}: {
  name: IconName;
  size?: number;
  className?: string;
  strokeWidth?: number;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox={VIEWBOX[name] ?? "0 0 24 24"}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      {PATHS[name]}
    </svg>
  );
}
